


























































































































































import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { mapState, mapMutations, mapActions } from "vuex";
import moment from "moment";
import VueApexCharts from "vue-apexcharts";

import {
  CLIENT_CHARTS_STORE_KEY,
  ClientChartMetric,
  CLIENT_CHART_METRICS,
} from "./_store";
import ProductCombobox from "@/components/Controls/ProductCombobox.vue";
import ProductContainerCombobox from "@/components/Controls/ProductContainerCombobox.vue";

@Component({
  components: {
    VueApexCharts,
    ProductCombobox,
    ProductContainerCombobox,
  },
  computed: {
    ...mapState(CLIENT_CHARTS_STORE_KEY, [
      "loading",
      "loaded",
      "fatalError",
      "activeMetric",
      "chartType",
      "targetDates",
      "product",
      "productContainer",
      "shipmentsSeries",
      "shipmentsOptions",
      "pricesSeries",
      "pricesOptions",
      "transportSeries",
      "transportOptions",
    ]),
  },
  methods: {
    ...mapMutations(CLIENT_CHARTS_STORE_KEY, [
      "SET_ACTIVE_METRIC",
      "SET_CHART_TYPE",
      "SET_TARGET_DATES",
      "SET_PRODUCT",
      "SET_PRODUCT_CONTAINER",
    ]),
    ...mapActions(CLIENT_CHARTS_STORE_KEY, ["loadChartData"]),
  },
})
export default class ClientCharts extends Vue {
  @Prop({ required: true }) clientId!: any;
  @Prop({ type: Boolean, default: false }) isModal!: boolean;
  activeMetric!: ClientChartMetric;
  chartType!: "line" | "bar" | "area";
  shipmentsSeries!: any[];
  pricesSeries!: any[];
  transportSeries!: any[];
  shipmentsOptions!: any;
  pricesOptions!: any;
  transportOptions!: any;
  loadChartData!: (clientId: any) => void;
  SET_ACTIVE_METRIC!: (m: ClientChartMetric) => void;
  SET_CHART_TYPE!: (type: "line" | "bar" | "area") => void;
  SET_TARGET_DATES!: (dates: Date[]) => void;
  SET_PRODUCT!: (product: any) => void;
  SET_PRODUCT_CONTAINER!: (container: any) => void;

  localMetric: ClientChartMetric = "shipments";
  localChartType: "line" | "bar" | "area" = "line";
  localTargetDates: Date[] = [];
  localProduct: any = null;
  localProductContainer: any = null;

  prevFilterSnapshot: string = "";

  mounted() {
    this.syncFromStore();
    this.restoreFromRoute();
    this.SET_TARGET_DATES([...this.localTargetDates]);
    this.SET_CHART_TYPE(this.localChartType);
    this.SET_ACTIVE_METRIC(this.localMetric);
    this.logMount();
    this.loadChartData(this.clientId);
  }

  @Watch("clientId")
  onClientIdChange() {
    this.loadChartData(this.clientId);
  }

  @Watch("chartType")
  onChartTypeFromStore(v: string) {
    this.localChartType = v as any;
  }

  @Watch("activeMetric")
  onActiveMetricFromStore(v: ClientChartMetric) {
    this.localMetric = v;
  }

  @Watch("localMetric")
  onLocalMetricChange(next: ClientChartMetric, prev: ClientChartMetric) {
    if (prev === undefined || next === prev) {
      return;
    }
    this.SET_ACTIVE_METRIC(next);
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[client-charts] metric_change", { metric: next });
    }
    this.persistRoute();
  }

  @Watch("localChartType")
  onLocalChartTypeChange(
    next: "line" | "bar" | "area",
    prev: "line" | "bar" | "area",
  ) {
    if (prev === undefined || next === prev) {
      return;
    }
    const storePrev = this.$store.state[CLIENT_CHARTS_STORE_KEY].chartType;
    this.SET_CHART_TYPE(next);
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[client-charts] chart_type_change", {
        previousType: storePrev,
        newType: next,
      });
    }
    this.persistRoute();
  }

  get hasTransportData() {
    return (
      this.transportSeries &&
      this.transportSeries.length > 0 &&
      this.transportSeries[0] &&
      Array.isArray(this.transportSeries[0].data) &&
      this.transportSeries[0].data.some((x: any) => x != null)
    );
  }

  get currentChartSeries() {
    switch (this.activeMetric) {
      case "shipments":
        return this.shipmentsSeries;
      case "prices":
        return this.pricesSeries;
      case "transport":
        return this.transportSeries;
      default:
        return [];
    }
  }

  get currentChartOptions() {
    switch (this.activeMetric) {
      case "shipments":
        return this.shipmentsOptions;
      case "prices":
        return this.pricesOptions;
      case "transport":
        return this.transportOptions;
      default:
        return {};
    }
  }

  get chartHasRenderableData() {
    if (this.activeMetric === "transport") {
      return this.hasTransportData;
    }
    return (
      Array.isArray(this.currentChartSeries) &&
      this.currentChartSeries.length > 0
    );
  }

  get chartRenderKey() {
    return `${this.activeMetric}-${this.chartType}`;
  }

  /** DocAPI id клиента для связки с заявкой (`d:clients:…`). */
  get chartsClientDocId(): string {
    return this.clientId != null ? String(this.clientId) : "";
  }

  get chartsComboboxKey(): string {
    return String(this.clientId || "no-client");
  }

  get currentEmptyText() {
    if (this.activeMetric === "shipments") {
      return "Нет данных по отгрузкам за период.";
    }
    if (this.activeMetric === "prices") {
      return "Нет данных по цене реализации за период.";
    }
    return "Нет данных за период.";
  }

  logMount() {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[client-charts] mount", {
        clientId: this.clientId,
        defaultRange: this.localTargetDates,
      });
    }
  }

  syncFromStore() {
    this.localMetric = this.$store.state[CLIENT_CHARTS_STORE_KEY].activeMetric;
    this.localChartType = this.$store.state[CLIENT_CHARTS_STORE_KEY].chartType;
    this.localTargetDates = [
      ...this.$store.state[CLIENT_CHARTS_STORE_KEY].targetDates,
    ];
    this.localProduct = this.$store.state[CLIENT_CHARTS_STORE_KEY].product;
    this.localProductContainer = this.$store.state[
      CLIENT_CHARTS_STORE_KEY
    ].productContainer;
  }

  restoreFromRoute() {
    if (this.isModal || !this.$route) {
      return;
    }
    const q = this.$route.query as any;
    let invalid = false;
    if (q.charts_df) {
      const d = moment(q.charts_df, "YYYY-MM-DD", true);
      if (d.isValid()) {
        this.localTargetDates[0] = d.toDate();
      } else {
        invalid = true;
      }
    }
    if (q.charts_dt) {
      const d = moment(q.charts_dt, "YYYY-MM-DD", true);
      if (d.isValid()) {
        this.localTargetDates[1] = d.toDate();
      } else {
        invalid = true;
      }
    }
    if (q.charts_type && ["line", "bar", "area"].indexOf(q.charts_type) >= 0) {
      this.localChartType = q.charts_type;
    } else if (q.charts_type) {
      invalid = true;
    }
    if (
      q.charts_metric &&
      (CLIENT_CHART_METRICS as string[]).indexOf(q.charts_metric) >= 0
    ) {
      this.localMetric = q.charts_metric as ClientChartMetric;
    } else if (q.charts_metric) {
      invalid = true;
    }
    if (invalid && process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[client-charts] invalid_query_filters", q);
    }
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[client-charts] state_restore", { query: q });
    }
  }

  persistRoute() {
    if (this.isModal || !this.$router) {
      return;
    }
    this.$router.replace({
      query: {
        ...this.$route.query,
        charts_df: moment(this.localTargetDates[0]).format("YYYY-MM-DD"),
        charts_dt: moment(this.localTargetDates[1]).format("YYYY-MM-DD"),
        charts_type: this.localChartType,
        charts_metric: this.localMetric,
      },
    });
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.debug("[client-charts] state_persist");
    }
  }

  onProductFilterInput(value: any) {
    this.localProduct = value;
    this.localProductContainer = null;
  }

  onProductContainerFilterInput(value: any) {
    this.localProductContainer = value;
  }

  applyFilters() {
    const snap = JSON.stringify({
      p: this.localProduct,
      c: this.localProductContainer,
      d0: moment(this.localTargetDates[0]).format("YYYY-MM-DD"),
      d1: moment(this.localTargetDates[1]).format("YYYY-MM-DD"),
    });
    if (
      snap !== this.prevFilterSnapshot &&
      process.env.NODE_ENV !== "production"
    ) {
      // eslint-disable-next-line no-console
      console.debug("[client-charts] filter_change", {
        prev: this.prevFilterSnapshot,
        next: snap,
      });
    }
    this.prevFilterSnapshot = snap;

    this.SET_TARGET_DATES([...this.localTargetDates]);
    this.SET_PRODUCT(this.localProduct);
    this.SET_PRODUCT_CONTAINER(this.localProductContainer);
    this.persistRoute();
    this.loadChartData(this.clientId);
  }
}
